import { useMeDetails, useMe } from "./user";
import {
  retrieveMeetingAccessRights,
  retrieveUserAccessRights,
} from "@sgdf/client-v2";
import { apiCall } from "@/core/utils/api";
import { useQuery } from "@tanstack/vue-query";
import { usePrevious } from "@vueuse/core";
import { unref } from "vue";
import { useMeetingId } from "./routes";
import { usePublicMeeting } from "./meeting";
import { useUserStore } from "./user-store";

export const useMeetingAccessRights = () => {
  const userId = useMe();
  const meetingId = useMeetingId();

  return useQuery({
    queryKey: ["access_rights", userId, meetingId],
    queryFn: () =>
      apiCall(
        retrieveMeetingAccessRights({ path: { id: unref(meetingId)! } }),
      ),
    enabled: () => unref(meetingId) != null,
    staleTime: Infinity,
  });
};

export const useUserAccessRights = () => {
  return useQuery({
    queryKey: ["access_rights"],
    queryFn: () => apiCall(retrieveUserAccessRights()),
    staleTime: Infinity,
  });
};

export const usePermission = () => {
  const meetingId = useMeetingId();
  const { data: user } = useMeDetails();
  const { data: meetingAccessRights, refetch } = useMeetingAccessRights();
  const { data: userAccessRights } = useUserAccessRights();
  const { data: currentMeeting } = usePublicMeeting();

  const previousMeeting = usePrevious(meetingId.value);

  watch(meetingId, () => {
    if (meetingId.value != null && previousMeeting.value !== meetingId.value) {
      refetch();
    }
  });

  const store = useUserStore();

  const {
    setModules,
    setMeetingPermissions,
    setUserPermissions,
    setIsSuperUser,
  } = store;

  watch(userAccessRights, () => {
    if (userAccessRights.value) {
      setUserPermissions(
        new Set([...(userAccessRights.value?.access_rights ?? [])]),
      );
    }
  });

  watch(user, () => {
    if (user.value?.is_superuser) {
      setIsSuperUser();
    }
  });

  watch(meetingAccessRights, () => {
    if (meetingId.value != null && meetingAccessRights.value) {
      setMeetingPermissions(
        new Set([...(meetingAccessRights.value?.access_rights ?? [])]),
        meetingId.value,
      );
    }
  });

  watch(currentMeeting, () => {
    if (currentMeeting.value) {
      setModules(
        new Set(
          currentMeeting.value?.modules?.map((module) => module.codename) ?? [],
        ),
        currentMeeting.value.id,
      );
    }
  });

  const { hasPermission } = storeToRefs(store);

  return hasPermission;
};
